/** Opaque severity ID as stored in sc_severities, e.g. 's0'. Use SeverityDto.label for display. */
export type ScorecardSeverity = string;

export interface SeverityDto {
  /** Opaque DB ID, e.g. 's0'. Use label for display. */
  id: string;
  /** Order rank: lower = more severe. */
  rank: number;
  /** Human-readable label from sc_severities, e.g. 'Critical'. */
  label: string;
}

export const CHECK_RESULTS = ['passed', 'failed'] as const;
export type CheckResult = (typeof CHECK_RESULTS)[number];

export interface LevelDto {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  lastSynced?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScorecardCheckDto {
  id: string;
  version: number;
  name: string;
  description?: string;
  severity: ScorecardSeverity;
  /** Opaque track ID (e.g. 't0'). Use trackName for display. */
  trackId: string;
  /** Resolved display name from sc_tracks, or the raw ID if no row exists. */
  trackName: string;
  /** Opaque level ID (e.g. 'l0'). */
  levelId?: string;
  /** Resolved display name from sc_levels; undefined when levelId is absent. */
  levelName?: string;
  purpose?: string;
  /** Raw owner name from YAML (e.g. 'teams-scalar-dynamite' or a user email without @zf.com). */
  owner?: string;
  active: boolean;
  lastSynced?: string;
  createdAt: string;
  updatedAt: string;
  checkJson?: Record<string, unknown>;
}

export interface ScorecardResultDto {
  snapshotTime: string;
  entityRef: string;
  team?: string;
  checkId: string;
  levelId: string | null;
  result: CheckResult;
  factValue?: unknown;
  checkVersion: number;
}

export interface SnapshotDto {
  snapshotTime: string;
  entityRef: string;
  team?: string;
  checkId: string;
  levelId: string | null;
  result: CheckResult;
  factValue?: unknown;
  checkVersion: number;
}

export interface TrackDto {
  id: string;
  name: string;
  description?: string;
  docsUrl?: string;
  /** Raw owner name from YAML (e.g. 'teams-scalar-dynamite' or a user email without @zf.com). */
  owner?: string;
}

export const UNTRACKED_NAME = 'Uncategorized';

export interface TeamTrackSummaryDto {
  /** null for checks without a track ("Uncategorized"). */
  id: string | null;
  /**
   * sc_tracks name, or the raw check track_id when no track definition
   * exists, or UNTRACKED_NAME.
   */
  name: string;
  description?: string;
  docsUrl?: string;
  /** Raw owner name from YAML (e.g. 'teams-scalar-dynamite' or a user email without @zf.com). */
  owner?: string;
  /**
   * round(100 × uniquePassingChecks / uniqueTotalChecks); null when no checks
   * are in scope. A check is "passing" only if every in-scope project passes it.
   */
  progress: number | null;
  /** Projects with at least one snapshot for a check in this track. */
  inScope: number;
  /**
   * Projects counted once, under their most severe failing check in this
   * track. Passing projects = inScope − sum of these.
   */
  failingBySeverity: Record<string, number>;
}

export interface TeamLevelSummaryDto {
  tracks: TeamTrackSummaryDto[];
  projects: TeamProjectDto[];
}

export interface TeamFailingCheckDto {
  checkId: string;
  name: string;
  severity: ScorecardSeverity;
  trackId: string | null;
  /** Opaque level ID, e.g. 'l0'. null when the check has no level. */
  levelId: string | null;
  /** Resolved level name. null when levelId is null. */
  levelName: string | null;
}

export interface TeamProjectDto {
  projectPath: string;
  projectName: string;
  entityRef: string;
  /** Catalog component whose gitlab.com/project-slug matches projectPath. */
  catalogEntityRef?: string;
  /** spec.type of that component. */
  kind?: string;
  /** spec.lifecycle of that component. */
  lifecycle?: string;
  passed: number;
  /** Applicable checks: those with a snapshot for this project. */
  total: number;
  lastCheckedAt: string;
  stale: boolean;
  /** Ordered Critical → Low, then by name. */
  failingChecks: TeamFailingCheckDto[];
}
